/**
 * AddTaskModal — the one Add Task form in the CRM.
 *
 * ⛔⛔ CARD CONTACT-PROFILE-INTAKE ITEM 12. Measured at d0cc1558, "Add Task"
 * meant two different broken things depending on where you pressed it:
 *
 *   CRM > Tasks tab          opened this modal, created a real task through
 *                            `crm:tasks:create` — AND THE TASK DID NOT APPEAR.
 *                            `onCreated` called CRMPage.loadData(); TasksTab
 *                            owns its own `taskList`, fetched from
 *                            getCRMFilteredList in an effect keyed on
 *                            `activeFilter`. loadData() cannot reach it. The
 *                            rep pressed Add Task, the modal closed, the list
 *                            was unchanged, and the only way to see the task
 *                            was to change a filter.
 *
 *   Contact record "+ Task"  called setTab('tasks') and nothing else. It landed
 *                            on TasksPane, which had NO create control at all.
 *                            A button with a plus icon that could not add
 *                            anything — "a dead end wearing an action's
 *                            clothes", in the words of the comment two lines
 *                            above it about a different button.
 *
 * ⚠ The channel was never the problem. `crm:tasks:create` is registered in
 * preload.js:173, public/apiClient.js:1366 and routes/crm.js:5144. This is not
 * the P71 shape (a safeAsync wrapper awaited without being called) and saying
 * so matters: the fix is a refresh signal and a missing button, not a rewire.
 *
 * ⇒ Lifted out of CRMPage.js so the contact record renders THIS modal rather
 * than growing a second one. One form, one payload, one error path.
 *
 * ⚠⚠ `onCreated` IS NOW CONTRACTUAL: every caller must refresh the list the
 * user is looking at. A caller that closes the modal and refreshes something
 * else reproduces the Tasks-tab bug exactly.
 *
 * ⛔ SYNTHETIC DATA ONLY in tests. No PHI in a task title, ever.
 */
import React, { useState } from 'react';
import { X } from 'lucide-react';
import {
  inp, btnPrimary, btnSm, overlayStyle, modalStyle, modalHeader, modalTitle,
  closeBtn, TASK_PRIORITY,
} from './crmShared';

export default function AddTaskModal({
  contacts, users, onClose, onCreated,
  prefillContactId = null, lockContact = false,
}) {
  // Foundation #5: optional recurrence (Never / Daily / Weekly / Monthly +
  // every-N interval + optional end date). Backend persists via migration 042
  // columns; on completion the next instance auto-spawns.
  const [form, setForm] = useState({
    // ⚠ Prefilled when the modal is opened FROM a contact record, so the rep
    // does not hunt their own patient out of a thousand-row select.
    contactId: prefillContactId ? String(prefillContactId) : '',
    patientId: '', title: '', description: '',
    priority: 'normal', dueDate: '', assignedTo: '',
    recurrenceRule: '', recurrenceInterval: 1, recurrenceEndDate: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const up = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    if (!form.title) return;
    setSaving(true);
    setError('');
    try {
      // Drop recurrence fields when "Never" — keeps the create payload clean
      // and avoids inserting interval=1 on non-recurring tasks (cosmetic).
      const payload = { ...form };
      if (!payload.recurrenceRule) {
        delete payload.recurrenceRule;
        delete payload.recurrenceInterval;
        delete payload.recurrenceEndDate;
      }
      /*
        ⛔⛔ THREE FAILURE SHAPES, AND THE OLD LINE SAW ONE.

        `if (r.error) throw new Error(r.message)` assumed a body with a
        `message`. secureHandler's refusal is { error: true, code, message } at
        HTTP 200 — fine — but server.js answers an unknown channel with
        { error: true, code: "NO_HANDLER" } and NO message, so the alert read
        "undefined"; and a missing body (`r` undefined) threw a TypeError whose
        message named a property, not the problem.

        ⚠ The text is whatever the server sent, down to the code. A NO_HANDLER
        names a channel and an ACCESS_DENIED names a permission; replacing
        either with a friendlier sentence hides which one it was.
      */
      const r = await window.zignagenixAPI.createCRMTask(payload);
      if (!r || r.error === true || r.success === false) {
        throw new Error((r && (r.message || r.code)) || 'the server did not confirm the task');
      }
      onCreated(r);
    } catch (err) {
      // ⛔ INLINE, NOT alert(). An alert is dismissed and gone; the rep needs
      // the reason still on screen next to the form they have to fix.
      setError(err?.message || 'Could not create the task.');
    }
    finally { setSaving(false); }
  }

  return (
    <div style={overlayStyle}>
      <div data-testid="add-task-modal" style={{ ...modalStyle, width: 480 }}>
        <div style={modalHeader}>
          <h2 style={modalTitle}>Add Task</h2>
          <button onClick={onClose} style={closeBtn}><X size={18} color="#ADB5BD" /></button>
        </div>
        <form onSubmit={submit}>
          <div style={{ marginBottom: 12 }}><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Title *</label>
            <input value={form.title} onChange={e => up('title', e.target.value)}
              aria-label="Title" required style={inp} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Contact</label>
              <select value={form.contactId} onChange={e => up('contactId', e.target.value)}
                disabled={lockContact} aria-label="Contact" style={inp}>
                <option value="">None</option>
                {contacts.map(c => <option key={c.id} value={c.id}>{c.last_name}, {c.first_name}</option>)}
              </select></div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Assigned To</label>
              <select value={form.assignedTo} onChange={e => up('assignedTo', e.target.value)} style={inp}>
                <option value="">Me</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.display_name}</option>)}
              </select></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Priority</label>
              <select value={form.priority} onChange={e => up('priority', e.target.value)} style={inp}>
                {Object.entries(TASK_PRIORITY).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select></div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Due Date</label>
              <input type="date" value={form.dueDate} onChange={e => up('dueDate', e.target.value)} style={inp} /></div>
          </div>
          <div style={{ marginBottom: 12 }}><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Description</label>
            <textarea value={form.description} onChange={e => up('description', e.target.value)} rows={2} style={{ ...inp, resize: 'vertical' }} /></div>
          {/* Foundation #5: recurrence */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Repeat</label>
              <select value={form.recurrenceRule} onChange={e => up('recurrenceRule', e.target.value)} style={inp}>
                <option value="">Never</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select></div>
            <div style={{ opacity: form.recurrenceRule ? 1 : 0.4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Every (N)</label>
              <input type="number" min="1" value={form.recurrenceInterval} disabled={!form.recurrenceRule}
                onChange={e => up('recurrenceInterval', parseInt(e.target.value, 10) || 1)} style={inp} />
            </div>
            <div style={{ opacity: form.recurrenceRule ? 1 : 0.4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>End Date</label>
              <input type="date" value={form.recurrenceEndDate} disabled={!form.recurrenceRule}
                onChange={e => up('recurrenceEndDate', e.target.value)} style={inp} />
            </div>
          </div>
          {error && (
            <div role="alert" data-testid="add-task-error" style={{
              marginBottom: 12, padding: '7px 10px', borderRadius: 6,
              background: '#FEE2E2', border: '1px solid #DC2626',
              color: '#991B1B', fontSize: 12, fontWeight: 600,
            }}>✗ Could not add the task — {error}</div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={btnSm}>Cancel</button>
            <button type="submit" data-testid="add-task-submit" disabled={saving}
              style={{ ...btnPrimary, background: '#F26522' }}>{saving ? 'Saving...' : 'Add Task'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
